/**
 * Der Befundverlauf: **was in dieser Revision gefunden wurde**, über die volle
 * Breite und ohne Vorbedingung — der Hauptinhalt der Revisionsansicht und nicht
 * ihr Beiwerk.
 *
 * Er ersetzt die schmale Leiste, die früher rechts neben dem Code stand. Der
 * Grund ist keine Geschmacksfrage: Ein Befund ist ein Absatz Fließtext, und eine
 * 320 Pixel breite Spalte mit eigener Scrollleiste zwingt ihn in vier Wörter je
 * Zeile. Wer wissen will, was die Agenten gefunden haben, las dort drei Befunde
 * und gab auf. Die **Gegenüberstellung** steht weiterhin darunter, aber
 * eingeklappt: Sie ist ein Nebenprodukt für den, der es sucht, und war nie die
 * Voraussetzung dafür, Befunde zu sehen.
 *
 * Fünf Festlegungen tragen ihn:
 *
 * - **Gruppiert nach Stelle, darin nach Zeit.** Eine Stelle ist Pfad und
 *   Zeilenbereich (ADR-0006). Darunter steht, wer wann was dazu gesagt hat —
 *   Befunde und Widerlegungen in einer Spur. So liest man, wie sich das Urteil
 *   über diese Zeilen entwickelt hat, statt es aus verstreuten Einträgen
 *   zusammenzusuchen. **Eine Stelle ist ein abgeschlossener Bereich**: Sie
 *   beginnt mit ihrer Kopfzeile auf abgesenkter Fläche — Ort, Zahl der Befunde,
 *   der Weg in den Code —, darunter stehen Ausschnitt und Einträge, und dann
 *   kommt die nächste. Vorher lagen alle Stellen als gleich aussehende Absätze
 *   untereinander, und wer bei der dritten war, wusste nicht mehr, zu welchem
 *   Ort der Befund gehörte, den er gerade las.
 * - **Die Stellen selbst stehen chronologisch**, nach ihrem ersten Eintrag. Die
 *   Reihenfolge ist damit die des Laufs und nicht die des Verzeichnisbaums:
 *   Womit ein Agent angefangen hat, steht oben.
 * - **Fremde Befunde stehen mit ihrem Urheber** (ADR-0013). Agent,
 *   Kontrollmuster und Kollege behaupten Verschiedenes. Ändern und Löschen gibt
 *   es nur am eigenen Befund — dieselbe Grenze zieht der Server.
 * - **Der geschlossene Modus wird benannt, wo er zutrifft** (ADR-0007,
 *   geschaltet durch ADR-0013). Welcher Fall gilt, entscheidet der Server und
 *   sagt es in `blind`; eine Oberfläche, die das selbst entschiede, hätte beide
 *   Fälle in der Hand, die niemals gleichzeitig gelten dürfen. **Und hier steht
 *   der Weg zurück** (v1.3-N-11): Wer die Blindabsicht gesetzt hat, merkt an
 *   dieser Stelle, dass nichts zu sehen ist — also gehört der Knopf hierher und
 *   nicht nur in die Zeile des Stroms, in der er gedrückt wurde.
 * - **Aus einem Befund wird hier ein Kontrollmuster** (ADR-0033, ADR-0012): Der
 *   Knopf steht am einzelnen Befund, sichtbar für `admin` und nicht am
 *   Kontrollbefund — der *ist* schon ein Muster. Er führt in dieselbe
 *   Musteranlage wie die Verwaltung, vorbelegt aus der Stelle; alles Weitere
 *   setzt ein Mensch.
 * - **Ein widerlegter Befund bleibt stehen** (Spec v1.3, Story 240) — hier sogar
 *   sichtbarer als vorher: Die Widerlegung ist ein eigener Eintrag im
 *   Zeitverlauf, mit Zeitpunkt und Begründung im Klartext, statt eines Schildes,
 *   dessen Text man erst suchen musste.
 *
 * Was hier **nicht** steht: Freigabe, Blockade, Gate. Das System qualifiziert
 * (Glossar, „Qualifizieren"); ein Befund hält keine Revision auf.
 *
 * **Keine eigene Werkzeugleiste** (#136): Die Revisionsansicht führt schon ihre
 * eine, mit dem Rückweg, der Übergabe und dem Schrittschalter (#41). Abgeben und
 * der Weg zurück aus dem geschlossenen Modus wirken auf **diesen Abschnitt**, den
 * Befundverlauf, und nicht auf den Screen als Ganzes — sie stehen deshalb im Kopf
 * der Sektion (`headerRight`, immer sichtbar) und nicht in einer zweiten Leiste.
 */

import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";

import { Button, Chip, Dialog, Icon, IconButton, Modal, Select, StatusPill, TextField } from "../../design-system";
import { ApiError, ForeignFixError } from "../../api/client";
import type {
  BlindWithdrawalApi,
  Finding,
  FindingApi,
  FindingKind,
  FindingOrigin,
  FindingQualification,
  FollowUpApi,
  FollowUpTally,
  PatternFromFindingApi,
  Refutation,
  RevisionFinding,
  Role,
  Severity,
  SiteAnchor,
  SiteExcerpt,
  SiteVerdictApi,
  SubmissionApi,
  Verdict,
} from "../../api/client";
import { formatClock, formatTimestamp } from "../../formatting";
import {
  judgedSite,
  labelOfJudgement,
  QUALIFICATION_LABELS,
  QUALIFICATION_TONES,
  REFUTATION_LABELS,
  REFUTATION_TONES,
  SEVERITY_TONES,
  STATUS_LABELS,
  STATUS_TONES,
  supersededSite,
  timeOf,
} from "../findings";
import { ListSection } from "../list";
import { PatternFromFinding } from "../pattern/PatternFromFinding";
import { WithdrawBlindButton } from "../blindReview";
import { Notice, describe, useLoaded } from "../loading";
import { bringIntoView } from "../scrolling";
import { Excerpt } from "./Excerpt";
import { anchorOfFinding, describeAnchor, siteAnchorOf, siteKeyOf } from "./anchor";
import { FollowUp, FollowUpDialog, followable, followUpOf } from "./followUp";
import type { FindingAnchor } from "./anchor";
import { OverwriteDialog, VerdictBar } from "./verdict";
import type { Overwrite } from "./verdict";

/** Die fünf Befundarten des Glossars, in der Reihenfolge, in der es sie führt. */
const KINDS: FindingKind[] = ["syntax", "konvention", "nicht-produktiv", "struktur", "migration"];

/** Die drei Schweregrade des Glossars, von leicht nach schwer. */
const SEVERITIES: Severity[] = ["hinweis", "mittel", "schwer"];

/**
 * Wie lang ein Titel höchstens sein darf. Dieselbe Grenze, die das
 * Antwortschema den Agenten setzt — was für die Maschine eine Zeile ist, ist es
 * für den Menschen auch.
 */
const TITLE_LIMIT = 80;

const CLOSED_MODE =
  "Geschlossener Modus: Hier stehen ausschließlich Ihre eigenen Befunde. " +
  "Die Befunde der Agenten und der Kollegen erscheinen erst nach Ihrer Abgabe.";

/**
 * Der offene Fall. Er steht dort, wo sonst der geschlossene Modus stünde, und
 * sagt dasselbe von der anderen Seite: was hier steht und woran es zu erkennen
 * ist. Ohne ihn hielte jemand die Liste für seine eigene.
 */
const OPEN_MODE =
  "Alle Befunde dieser Revision — Ihre eigenen und die der Agenten, der Kontrolle " +
  "und Ihrer Kollegen. Der Urheber steht an jedem Eintrag; ändern und löschen " +
  "lassen sich nur die eigenen.";

const SUBMITTED = "Abgegeben. Ihre Befunde stehen fest und sind nicht mehr änderbar.";

const SUBMIT_WARNING =
  "Mit der Abgabe steht der Zeitpunkt fest, ab dem Ihre Befunde unbeeinflusst sind. " +
  "Danach lassen sie sich weder ändern noch löschen, und die Gegenüberstellung mit " +
  "den Agenten wird sichtbar. Eine Revision ohne Befund abzugeben ist ausdrücklich " +
  "vorgesehen — mir ist nichts aufgefallen ist eine festgehaltene Aussage und keine Lücke.";

const NOTHING_MARKED = "Erst eine Zeile im Code markieren, dann den Befund anlegen.";

/**
 * Was dasteht, wenn nach einer Bewertung keine unbewertete Stelle mehr übrig ist
 * (#187).
 *
 * Der Satz spricht von der **Revision** und nicht von der Liste: „Kein weiterer
 * Eintrag" wäre eine Auskunft über die Datenlage, und die hat der Anwender vor
 * sich. Was er wissen will, ist, ob er hier fertig ist.
 */
const NOTHING_OPEN = "In dieser Revision ist keine Stelle mehr unbewertet.";

/** Woher ein Befund kommt — das Wort daneben, nicht die Kennung. */
const ORIGINS: Record<FindingOrigin, string> = {
  agent: "Agent",
  pattern: "Kontrollmuster",
  person: "Person",
};

/**
 * Was gerade im Formular steht. `id` ist der Befund, der geändert wird — `null`
 * heißt: ein neuer entsteht.
 */
interface Draft {
  id: number | null;
  kind: FindingKind | null;
  severity: Severity | null;
  body: string;
  title: string;
}

const EMPTY: Draft = { id: null, kind: null, severity: null, body: "", title: "" };

/**
 * Ein Eintrag im Zeitverlauf einer Stelle: ein Befund oder eine Widerlegung.
 *
 * Beide in einer Spur und nicht in zwei: „widerlegt" bezieht sich auf den
 * Befund darüber, und getrennte Listen zwängen den Leser, die Reihenfolge selbst
 * herzustellen.
 */
type Event =
  | { at: number; order: number; kind: "finding"; finding: RevisionFinding }
  | { at: number; order: number; kind: "refutation"; finding: RevisionFinding; refutation: Refutation };

/** Eine Stelle — Pfad und Zeilenbereich (ADR-0006) — mit ihrem Zeitverlauf. */
interface Site {
  key: string;
  finding: RevisionFinding;
  /** Alle Befunde dieser Stelle. Der Handgriff gilt für sie zusammen (ADR-0022). */
  found: RevisionFinding[];
  events: Event[];
  at: number;
  /**
   * Der **Ausschnitt** zu dieser Stelle — ihre Zeilen samt Zusammenhang (Glossar,
   * „Ausschnitt"). `null` heißt: Der Server hat zu diesem Anker keinen mitgeliefert.
   *
   * Zusammengeführt wird über den Anker und nicht über eine Kennung: Eine Stelle
   * wird bei der Abfrage gerechnet und hat keine (ADR-0015).
   */
  excerpt: SiteExcerpt | null;
}

export function FindingLog({
  client,
  revisionId,
  role,
  anchor,
  target = null,
  fileChangeId,
  composing,
  onComposing,
  onJump,
  onSubmitted,
}: {
  client: FindingApi
    & SubmissionApi
    & SiteVerdictApi
    & FollowUpApi
    & BlindWithdrawalApi
    & PatternFromFindingApi;
  revisionId: number;
  /**
   * Die eigene Rolle — sie entscheidet allein darüber, ob der Knopf „Muster
   * daraus machen" dasteht.
   *
   * Eine Frage der Anzeige und keine Rechteentscheidung: Die trifft der Server
   * bei jeder Anfrage, und ein `dev`, der den Weg von Hand ginge, bekäme dort
   * eine 403.
   */
  role: Role;
  /** Der Anker der aktuellen Markierung — `null`, solange nichts markiert ist. */
  anchor: FindingAnchor | null;
  /**
   * Die Stelle, aus der heraus diese Ansicht geöffnet wurde — der Sprung aus
   * dem **Befundvorrat**.
   *
   * Sie wird hervorgehoben und angescrollt. Ohne das landet man in einer
   * Revision mit dreißig Befunden und sucht den einen, auf den man geklickt hat
   * — der Sprung führte an die Datei und nicht an den Befund.
   *
   * Gefiltert wird deshalb ausdrücklich **nicht**: Was sonst noch in dieser
   * Revision gefunden wurde, gehört zum Bild, in dem der eine Befund steht.
   */
  target?: FindingAnchor | null;
  /** Die offene Dateiänderung; sie sagt dem Server, wo der Anker herkommt. */
  fileChangeId: number | null;
  /**
   * Soll das Formular für einen **neuen** Befund offen stehen?
   *
   * Der Schalter liegt außen, weil er von zwei Stellen aus umgelegt wird: von
   * hier oben und von der Markierungszeile unten am Code. Läge er innen, müsste
   * die Markierungszeile in diese Komponente hineinrufen.
   */
  composing: boolean;
  onComposing: (composing: boolean) => void;
  /**
   * Der Weg von einer Stelle in den Code.
   *
   * Er führt in die **eine** Codefläche dieser Ansicht — die oben, bei den
   * Dateien der Revision. Sie trägt die Markierung, aus der ein Befund entsteht;
   * eine zweite Fläche hier hätte eine zweite, und der Befund entstünde an der,
   * die der Anwender gerade nicht ansieht.
   */
  onJump: (finding: Finding) => void;
  /** Die Gegenüberstellung soll danach erscheinen — sie lädt selbst neu. */
  onSubmitted: () => void;
}): ReactElement {
  const { state, reload } = useLoaded(() => client.findings(revisionId));
  const [editing, setEditing] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  // Die eine Rückfrage: Ein fremdes „behoben" würde überschrieben. Sie hält den
  // Handgriff fest, der abgewiesen wurde — bestätigt wird derselbe und kein neuer.
  const [overwriting, setOverwriting] = useState<Overwrite | null>(null);
  // Der Befund, zu dem gerade eine Begründung geschrieben wird — `null` heißt:
  // Das Formular steht nicht offen.
  const [followingUp, setFollowingUp] = useState<RevisionFinding | null>(null);

  const loaded = state.kind === "loaded" ? state.value : null;
  const findings = loaded?.findings ?? [];
  const followUps = loaded?.followUpMarks ?? [];
  const submitted = loaded?.submitted ?? false;
  const blind = loaded?.blind ?? false;
  // Ob es etwas zurückzunehmen gibt, sagt der Server: Wer die Eigenschaft
  // `blind_review` trägt, arbeitet blind, ohne je einen Knopf gedrückt zu haben,
  // und für ihn führt hier kein Weg heraus (v1.3-N-11).
  const blindRequested = loaded?.blindRequested ?? false;
  const own = findings.filter((one) => one.own);
  const sites = sitesOf(findings, loaded?.excerpts ?? []);
  const targetKey = target === null ? null : siteKeyOf(target);

  // Der einmalige Sprung an die Stelle aus dem Befundvorrat lebt hier und
  // nicht im `SiteBlock`: `reload()` räumt `sites` kurz leer (`loaded` wird
  // `null`), jeder `SiteBlock` verschwindet aus dem Baum und entsteht mit der
  // Antwort neu — ein Effekt *in ihm* liefe darum nach jeder Bewertung wieder
  // an. `FindingLog` bleibt über all das montiert, also merkt sich hier ein
  // Verweis den zuletzt angesprungenen Schlüssel. Wechselt `target` wirklich
  // — andere Revision, anderer Anker —, ist der Schlüssel ein anderer und der
  // Sprung wieder fällig.
  const siteElements = useRef(new Map<string, HTMLElement>());
  const jumpedTo = useRef<string | null>(null);

  useEffect(() => {
    if (targetKey === null || jumpedTo.current === targetKey) {
      return;
    }

    const element = siteElements.current.get(targetKey);

    if (element !== undefined) {
      // An den Anfang des Bereichs und nicht in seine Mitte: Eine Stelle mit
      // langem Ausschnitt und mehreren Befunden ist höher als das Fenster;
      // mittig gestellt beginnt sie oberhalb des Bildrands, und man sieht ihre
      // Mitte und hält sie für das Ende. Gelesen wird von oben nach unten,
      // also fängt der Sprung oben an.
      bringIntoView(element, "start");
      jumpedTo.current = targetKey;
    }
  });

  /**
   * Der **Weiterlauf** nach einer gesetzten Bewertung (#187): der Schlüssel der
   * Stelle, von der aus gesucht wird, und ob die Antwort dazu schon da ist.
   *
   * Gesprungen wird erst auf dem neuen Stand. `decide()` lädt neu, und bis die
   * Antwort da ist, steht noch der Stand von vor dem Handgriff — darin sähe die
   * eben beurteilte Stelle unbeurteilt aus. Dazwischen verschwindet die ganze
   * Liste kurz aus dem Baum (`loaded` ist `null`), und ein Scrollstand von
   * vorher wäre mit ihr weg. `reloaded` hält deshalb fest, dass diese
   * Zwischenstufe durchlaufen ist.
   */
  const advance = useRef<{ from: string; reloaded: boolean } | null>(null);

  /**
   * Ob die letzte Bewertung nichts Unbewertetes mehr übrig gelassen hat.
   *
   * Kein Rechenergebnis aus den Befunden, sondern das Ergebnis **eines Griffs**:
   * Wer eine längst durchgearbeitete Revision nur öffnet, hat gerade nichts
   * abgeschlossen, und der Satz wäre eine Meldung ohne Anlass.
   */
  const [nothingOpen, setNothingOpen] = useState(false);

  useEffect(() => {
    const pending = advance.current;

    if (pending === null) {
      return;
    }

    if (state.kind === "loading") {
      pending.reloaded = true;

      return;
    }

    // Noch der Stand von vor dem Handgriff: Der Ladevorgang hat nicht einmal
    // begonnen, und was hier zu sehen ist, ist die Liste, die gleich verschwindet.
    if (!pending.reloaded) {
      return;
    }

    advance.current = null;

    // Ist das Neuladen gescheitert, steht statt der Liste der Wortlaut des
    // Fehlers. Weitergereicht wird dann niemand — es gibt keine Stellen, an die.
    if (state.kind !== "loaded") {
      return;
    }

    const next = nextOpenSite(sites, pending.from);

    if (next === null) {
      setNothingOpen(true);

      return;
    }

    const element = siteElements.current.get(next.key);

    if (element !== undefined) {
      // An den Anfang des Bereichs, aus demselben Grund wie oben — und **ohne
      // weiche Bewegung**: Diesen Sprung hat niemand ausgelöst, um ihm
      // zuzusehen; er steht zwischen zwei Handgriffen (`views/scrolling.ts`,
      // `Motion`).
      bringIntoView(element, "start", "instant");
    }
  });

  /**
   * Der Knopf **„Muster daraus machen"** zu einem Befund — oder keiner.
   *
   * Er steht nur für `admin` da: Das Anlegen ist eine Schreiboperation an der
   * Musterverwaltung. Und er steht nicht am **Kontrollbefund** — der *ist* schon
   * ein Muster, und ein Muster aus ihm wäre dasselbe ein zweites Mal. Dieselbe
   * Grenze zieht das Nachhalten (ADR-0012).
   */
  const patternHandle = (found: RevisionFinding): ReactElement | null =>
    role === "admin" && found.origin !== "pattern" ? (
      <PatternFromFinding client={client} findingId={found.id} />
    ) : null;

  /** Das Formular schließen — gleich, ob es einen neuen oder einen alten Befund trug. */
  const close = (): void => {
    setEditing(null);
    onComposing(false);
  };

  /**
   * `resets` sagt, ob das Formular danach zu ist. Beim Anlegen und Ändern ja —
   * beim Vermerk „behoben" nicht: Er gehört zu einem anderen Befund, und einen
   * halb getippten Freitext dabei wegzuwerfen wäre für niemanden das Gewollte.
   */
  const run = async (action: () => Promise<unknown>, resets = true): Promise<void> => {
    setBusy(true);
    setError(null);
    // Jede Änderung am Bestand kann den Satz „hier ist nichts mehr offen"
    // überholen — ein neuer Befund, eine zurückgenommene Bewertung. Er gilt für
    // den Stand, zu dem er entstanden ist, und für keinen danach.
    setNothingOpen(false);

    try {
      await action();

      if (resets) {
        close();
      }

      reload();
    } catch (failure) {
      setError(describe(failure));

      // Wer in der Zwischenzeit abgegeben hat, soll die Ansicht umgeschaltet
      // sehen und nicht nur den Satz dazu. Nur bei diesem Code neu geladen: Bei
      // „nicht verbunden" ersetzte der zweite Fehlschlag den ersten Wortlaut.
      if (failure instanceof ApiError && failure.code === "already_submitted") {
        reload();
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Die Stelle **bewerten** (ADR-0022). Ein Handgriff, beide Achsen, alle Befunde
   * darunter.
   *
   * Er geht **nicht** über `run()`: Die eine Rückfrage ist kein Fehler, den man
   * hinschreibt, sondern eine Frage, die man stellt — stünde sie als roter Satz
   * in der Fläche, hätte der Anwender gelesen, dass etwas schiefging, und keinen
   * Weg, es trotzdem zu tun.
   */
  const decide = (anchor: SiteAnchor, verdict: Verdict, force: boolean): void => {
    setBusy(true);
    setError(null);
    setNothingOpen(false);

    client.setSiteVerdict(anchor, verdict, force).then(
      () => {
        setBusy(false);
        setOverwriting(null);
        // Weiter zur nächsten unbewerteten Stelle (#187) — angemeldet erst hier
        // und nicht beim Klick: Vorher gibt es keine Bewertung, von der aus es
        // eine nächste gäbe, und ein abgewiesener Handgriff soll niemanden
        // weiterreichen.
        advance.current = { from: siteKeyOf(anchor), reloaded: false };
        reload();
      },
      (failure: unknown) => {
        setBusy(false);

        if (failure instanceof ForeignFixError) {
          setOverwriting({ anchor, verdict, refused: failure });

          return;
        }

        setOverwriting(null);
        setError(describe(failure));
      },
    );
  };

  /** Die Rücknahme: leer und offen, an allen Befunden der Stelle. */
  const withdraw = (anchor: SiteAnchor): void => {
    void run(() => client.withdrawSiteVerdict(anchor), false);
  };

  /**
   * Die Blindabsicht zurücknehmen (v1.3-N-11).
   *
   * Über `run()` und ohne das Formular zu schließen: Der Knopf gehört nicht zu
   * einem Befund, und ein halb getippter Freitext wäre hier für niemanden das
   * Gewollte. Neu geladen wird dabei ohnehin — und genau darum geht es: Die
   * fremden Befunde erscheinen sofort und nicht erst beim nächsten Öffnen.
   */
  const withdrawBlind = (): void => {
    void run(() => client.withdrawBlindReview(revisionId), false);
  };

  const save = (draft: Draft): void => {
    const { id, kind, severity } = draft;
    const body = draft.body.trim();
    const title = draft.title.trim();

    if (kind === null || severity === null || body === "" || title === "") {
      return;
    }

    const qualification: FindingQualification = { kind, severity, body, title };

    if (id !== null) {
      void run(() => client.updateFinding(revisionId, id, qualification));

      return;
    }

    if (anchor === null || fileChangeId === null) {
      return;
    }

    void run(() =>
      client.createFinding(revisionId, {
        fileChangeId,
        revisionNumber: anchor.revision,
        startLine: anchor.startLine,
        endLine: anchor.endLine,
        ...qualification,
      }),
    );
  };

  /**
   * Die Abgabe. Sie ist ausdrücklich und wird deshalb bestätigt: Was danach
   * unveränderlich ist, soll niemand mit einem Fehlklick festschreiben.
   */
  const submit = (): void => {
    setAsking(false);

    void run(async () => {
      await client.submit(revisionId);
      onSubmitted();
    });
  };

  // Nach der Abgabe gibt es nichts zu tippen — auch dann nicht, wenn das
  // Formular gerade offen stand: Wer abgegeben hat, während er schrieb, erfährt
  // es an dieser Stelle, und ein Formular, dessen Knopf der Server ablehnt,
  // wäre ein Versprechen, das niemand einlöst.
  const draft = submitted ? null : (editing ?? (composing ? EMPTY : null));

  return (
    <ListSection
      title="Befunde"
      // Das Schloss steht für den geschlossenen Modus und nicht für die Abgabe:
      // Es ist zu, solange fremde Befunde zurückgehalten werden.
      glyph={blind && !submitted ? "lock" : "lock-open"}
      headerRight={
        <span style={{ display: "inline-flex", alignItems: "center", gap: "var(--sp-4)" }}>
          <Chip>{String(findings.length)}</Chip>

          {/* Der **Weg zurück** aus dem geschlossenen Modus (v1.3-N-11) — hier,
              wo das Schloss steht und der Satz, der ihn benennt: die Stelle, an
              der man merkt, dass nichts zu sehen ist. Nur solange nicht
              abgegeben ist und nur, wenn die Blindheit aus der Absicht kommt;
              nach der Abgabe wäre die Rücknahme eine nachträgliche Änderung der
              Bedingungen einer abgeschlossenen Durchsicht. */}
          {blind && blindRequested && !submitted ? (
            <WithdrawBlindButton busy={busy} onWithdraw={withdrawBlind} />
          ) : null}

          {/* Zum **Anlegen** steht hier kein Knopf: Ein Befund braucht eine
              Markierung, und markiert wird oben im Code. Der Knopf steht dort,
              wo die Markierung entsteht — hier stünde er ohne das, was er
              braucht, und wäre die meiste Zeit grau. */}

          {/* Auch ohne einen einzigen Befund abgebbar: Die Schaltfläche hängt
              ausdrücklich nicht an der Zahl der Befunde. */}
          {submitted || loaded === null ? null : (
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              icon="send"
              onClick={() => setAsking(true)}
            >
              Revision abgeben
            </Button>
          )}
        </span>
      }
    >
      <p
        style={{
          margin: 0,
          padding: "0 var(--sp-5) var(--sp-5)",
          maxWidth: "78ch",
          fontSize: "var(--fs-11)",
          lineHeight: "var(--lh-normal)",
          color: "var(--fg-6)",
          textWrap: "pretty",
        }}
      >
        {submitted ? SUBMITTED : blind ? CLOSED_MODE : OPEN_MODE}
      </p>

      <div style={{ padding: "0 var(--sp-5)" }}>
        {state.kind === "loading" ? <Notice text="Wird geladen ..." glyph="timer" /> : null}
        {state.kind === "failed" ? (
          <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
        ) : null}
        {error === null ? null : (
          <Notice text={error} glyph="triangle-alert" color="var(--ac-red)" />
        )}
        {loaded !== null && findings.length === 0 ? (
          <Notice
            text={
              blind
                ? "Noch kein eigener Befund in dieser Revision."
                : "Noch kein Befund in dieser Revision."
            }
          />
        ) : null}
      </div>

      {sites.map((site) => (
        <SiteBlock
          key={site.key}
          site={site}
          marked={site.key === targetKey}
          elementRef={(element) => {
            if (element === null) {
              siteElements.current.delete(site.key);
            } else {
              siteElements.current.set(site.key, element);
            }
          }}
          submitted={submitted}
          busy={busy}
          followUps={followUps}
          patternHandle={patternHandle}
          onFollowUp={setFollowingUp}
          onWithdrawFollowUp={(found) =>
            void run(() => client.withdrawFollowUpMark(found.id), false)
          }
          onJump={onJump}
          onDecide={(verdict) => decide(siteAnchorOf(site.finding), verdict, false)}
          onWithdraw={() => withdraw(siteAnchorOf(site.finding))}
          onEdit={(found) =>
            setEditing({
              id: found.id,
              kind: found.kind,
              severity: found.severity,
              body: found.body,
              title: found.title,
            })
          }
          onDelete={(found) => void run(() => client.deleteFinding(revisionId, found.id))}
        />
      ))}

      {/* Am **Fuß** der Liste und nicht oben bei den übrigen Meldungen: Der Satz
          steht da, weil die Seite ausdrücklich stehen bleibt (#187), und wer
          gerade die letzte Stelle bewertet hat, steht in der Liste und nicht an
          ihrem Kopf. Eine Zeile über dem ersten Block sähe er nicht. */}
      {nothingOpen ? (
        <div style={{ padding: "0 var(--sp-5)" }}>
          <Notice text={NOTHING_OPEN} glyph="check" />
        </div>
      ) : null}

      {draft === null ? null : (
        <Composer
          draft={draft}
          anchor={draft.id === null ? anchor : anchorOfSaved(own, draft.id)}
          busy={busy}
          onClose={close}
          onSave={save}
        />
      )}

      {asking ? (
        <Modal onClose={() => setAsking(false)}>
          <Dialog
            title="Revision abgeben"
            description={SUBMIT_WARNING}
            width={560}
            onClose={() => setAsking(false)}
            footer={
              <>
                <Button variant="outline" size="lg" onClick={() => setAsking(false)}>
                  Abbrechen
                </Button>
                <Button variant="primary" size="lg" disabled={busy} onClick={submit}>
                  Abgeben
                </Button>
              </>
            }
          >
            <span style={{ fontSize: "var(--fs-12)", color: "var(--text-body)" }}>
              {own.length === 0
                ? "Sie geben ohne Befund ab."
                : `Sie geben mit ${String(own.length)} eigenen Befund${own.length === 1 ? "" : "en"} ab.`}
            </span>
          </Dialog>
        </Modal>
      ) : null}

      {overwriting === null ? null : (
        <OverwriteDialog
          overwriting={overwriting}
          busy={busy}
          onClose={() => setOverwriting(null)}
          onConfirm={() => decide(overwriting.anchor, overwriting.verdict, true)}
        />
      )}

      {followingUp === null ? null : (
        <FollowUpDialog
          title={describeAnchor(anchorOfFinding(followingUp))}
          busy={busy}
          onClose={() => setFollowingUp(null)}
          onSave={(reason) => {
            const found = followingUp;
            setFollowingUp(null);

            void run(() => client.addFollowUpMark(found.id, reason), false);
          }}
        />
      )}
    </ListSection>
  );
}

/**
 * Die Befunde einer Revision als Stellen mit Zeitverlauf.
 *
 * Die Stelle ist Pfad **und** Zeilenbereich und nicht nur der Pfad: Zwei Befunde
 * in derselben Datei an verschiedenen Zeilen sind zwei Sachverhalte, und sie
 * unter einer Überschrift zu führen behauptete einen Zusammenhang, den es nicht
 * gibt.
 *
 * Sortiert wird nach dem **ersten** Eintrag einer Stelle. Wo kein Zeitpunkt
 * mitgeliefert wurde, entscheidet die Kennung — sie steigt mit der Zeit, und
 * eine Stelle ohne Zeitpunkt ans Ende zu schieben verlöre die einzige
 * Reihenfolge, die dann noch da ist.
 *
 * Der **Ausschnitt** kommt aus derselben Antwort und wird über den Anker
 * zugeordnet — dieselbe Regel, aus der die Stellen selbst entstehen. Fehlt einer,
 * bleibt die Stelle ohne: Der Server liefert keinen erfundenen, und diese Ansicht
 * erfindet auch keinen.
 */
export function sitesOf(findings: RevisionFinding[], excerpts: SiteExcerpt[] = []): Site[] {
  const sites = new Map<string, Site>();
  const found = new Map(excerpts.map((excerpt) => [siteKeyOf(excerpt), excerpt]));

  for (const finding of findings) {
    const key = siteKeyOf(finding);
    const events: Event[] = [
      { at: timeOf(finding.createdAt), order: finding.id, kind: "finding", finding },
      ...finding.refutations.map((refutation, position) => ({
        at: timeOf(refutation.createdAt ?? finding.createdAt),
        // Die Widerlegung reiht sich hinter ihren Befund: Ohne eigenen
        // Zeitpunkt stünde sie sonst vor ihm, und das hat sie nie getan.
        order: finding.id + (position + 1) / (finding.refutations.length + 1),
        kind: "refutation" as const,
        finding,
        refutation,
      })),
    ];

    const known = sites.get(key);

    if (known === undefined) {
      sites.set(key, {
        key,
        finding,
        found: [finding],
        events,
        at: events[0]?.at ?? 0,
        excerpt: found.get(key) ?? null,
      });

      continue;
    }

    known.found.push(finding);
    known.events.push(...events);
  }

  const ordered = [...sites.values()];

  for (const site of ordered) {
    site.events.sort(byTime);
    site.at = site.events[0]?.at ?? 0;
  }

  ordered.sort((one, other) => one.at - other.at || one.key.localeCompare(other.key));

  return ordered;
}

/**
 * Die **nächste unbewertete Stelle** nach `from` (#187) — oder `null`, wenn es in
 * dieser Revision keine mehr gibt.
 *
 * Gesucht wird **im Kreis**: hinter `from` weiter und, wenn dort nichts mehr
 * steht, von vorn bis `from`. Ohne den Kreis wäre der Satz „hier ist nichts mehr
 * offen" gelegentlich falsch — wer die unterste Stelle zuerst bewertet, läse ihn,
 * während über ihm noch fünf unbewertete stehen. Die Reihenfolge ist die des
 * Verlaufs und damit die des Laufs, nicht die des Verzeichnisbaums.
 *
 * Übersprungen wird beides, was keinen Handgriff mehr trägt: das bereits
 * Bewertete und das **Überschriebene**. An einer überschriebenen Stelle nimmt der
 * Server nichts mehr an, und ein Sprung dorthin führte an einen Ort, an dem
 * nichts zu tun ist.
 *
 * Die Revision wird dabei nicht verlassen — auch dann nicht, wenn hier nichts
 * mehr offen ist. Der Weg entlang des **Befundvorrats** ist ein eigener, und er
 * braucht einen Filter, den es noch nicht gibt.
 */
function nextOpenSite(sites: Site[], from: string): Site | null {
  const at = sites.findIndex((site) => site.key === from);

  if (at < 0) {
    return null;
  }

  const behind = [...sites.slice(at + 1), ...sites.slice(0, at)];

  return behind.find((site) => !judgedSite(site.found) && !supersededSite(site.found)) ?? null;
}

function byTime(one: Event, other: Event): number {
  const known = Number.isNaN(one.at) || Number.isNaN(other.at) ? 0 : one.at - other.at;

  return known !== 0 ? known : one.order - other.order;
}

/** Der Anker des Befundes, der gerade geändert wird — er bleibt, wo er war. */
function anchorOfSaved(findings: RevisionFinding[], id: number): FindingAnchor | null {
  const found = findings.find((one) => one.id === id);

  return found === undefined ? null : anchorOfFinding(found);
}

/**
 * Eine Stelle als **abgeschlossener Bereich**: Kopfzeile, **Ausschnitt**, und
 * darunter, wer wann was gesagt hat.
 *
 * **Die Kopfzeile trennt.** Sie steht auf abgesenkter Fläche, über einer
 * Haarlinie, und trägt alles, was für die ganze Stelle gilt: den Ort, die Zahl
 * der Befunde, den Weg in den Code. Darunter beginnt der Inhalt dieser einen
 * Stelle und hört mit der nächsten Kopfzeile wieder auf. Vorher unterschieden
 * sich zwei Stellen nur durch eine Haarlinie zwischen lauter gleich aussehenden
 * Absätzen — bei der dritten wusste niemand mehr, wo er war.
 *
 * Der Ort ist zugleich die Sprungmarke; der Knopf daneben schreibt aus, wohin
 * sie führt. Beides denselben Weg, weil ein Ort, den man anklicken kann, ohne
 * dass es dasteht, für die Hälfte der Leser keiner ist.
 *
 * **Der Ausschnitt steht zwischen Kopfzeile und Befunden**, und das ist die
 * Aussage dieses Bereichs: „Rechnung.php @ r4711 · Zeilen 42–44" nennt einen
 * Ort, und unmittelbar darunter steht, was dort steht.
 *
 * **Der Code der Datei steht nicht hier.** Er steht oben, in der einen
 * Codefläche der Ansicht, und der Sprung führt dorthin — mit markierten Zeilen.
 * Aufgeklappt an jeder Stelle schob er die Befunde um eine halbe Bildschirmhöhe
 * nach unten, und zwei offene Flächen hätten zwei Markierungen gehabt.
 */
function SiteBlock({
  site,
  marked,
  elementRef,
  submitted,
  busy,
  followUps,
  patternHandle,
  onFollowUp,
  onWithdrawFollowUp,
  onJump,
  onEdit,
  onDelete,
  onDecide,
  onWithdraw,
}: {
  site: Site;
  /**
   * Ist das die Stelle, aus der heraus die Ansicht geöffnet wurde? Dann wird sie
   * hervorgehoben.
   */
  marked: boolean;
  /**
   * Meldet das Wurzelelement dieser Stelle an `FindingLog` — dort und nicht
   * hier lebt der einmalige Sprung, denn dieser Block verschwindet bei jedem
   * Neuladen aus dem Baum und entsteht danach neu.
   */
  elementRef: (element: HTMLElement | null) => void;
  submitted: boolean;
  busy: boolean;
  /** Der **Vermerkstand** aller Befunde dieser Antwort (Glossar, „Nachhalten"). */
  followUps: FollowUpTally[];
  /**
   * Der Knopf „Muster daraus machen" zu einem Befund — `null`, wo es keinen
   * gibt. Gebaut wird er oben, wo Rolle und Client stehen; hier steht nur, an
   * welche Zeile er gehört.
   */
  patternHandle: (finding: RevisionFinding) => ReactElement | null;
  onFollowUp: (finding: RevisionFinding) => void;
  onWithdrawFollowUp: (finding: RevisionFinding) => void;
  onJump: (finding: RevisionFinding) => void;
  onEdit: (finding: RevisionFinding) => void;
  onDelete: (finding: RevisionFinding) => void;
  /** Die Stelle bewerten — ein Handgriff, beide Achsen, alle Befunde darunter. */
  onDecide: (verdict: Verdict) => void;
  onWithdraw: () => void;
}): ReactElement {
  return (
    <article
      ref={elementRef}
      aria-label={describeAnchor(anchorOfFinding(site.finding))}
      aria-current={marked ? "true" : undefined}
      style={{
        display: "flex",
        flexDirection: "column",
        // Luft vor jedem Block: Weißraum trennt stärker als jede Linie und
        // nimmt den Schweregraden im Inneren keine Farbe weg.
        marginTop: "var(--sp-9)",
        // Die kräftige Linie oben ist die Grenze des Blocks — die Haarlinie
        // unter der Kopfzeile bleibt subtil, damit Kopf und Inhalt eines
        // Blocks weiter zusammengehören.
        borderTop: "1px solid var(--line-b)",
        // Die Hervorhebung ist ein Streifen am Rand und keine Einfärbung: Sie
        // sagt „hier bist du gelandet", ohne den Schweregraden die Farbe
        // streitig zu machen, die in dieser Ansicht etwas anderes bedeutet —
        // aus demselben Grund bleibt auch die Blockgrenze eine Linie und keine
        // Fläche. Dieselbe Haarlinie wie an der markierten Codezeile — zweimal
        // dasselbe Zeichen für dieselbe Sache.
        borderLeft: `1px solid ${marked ? "var(--ac-blue)" : "transparent"}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "var(--sp-4)",
          padding: "var(--sp-4) var(--sp-5)",
          background: "var(--bg-sunken)",
          borderBottom: "1px solid var(--line-f)",
        }}
      >
        <button
          type="button"
          title="Zu dieser Stelle im Code springen"
          onClick={() => onJump(site.finding)}
          style={{
            flex: 1,
            minWidth: 0,
            padding: 0,
            border: "none",
            background: "transparent",
            color: "var(--fg-1)",
            fontFamily: "var(--font-sans)",
            // Der Ort führt den Block typografisch an — Grad und Gewicht hoch,
            // statt ihn über eine Linie zu erkennen. Ungeteilt in einem
            // einzigen Textknoten: Der zugängliche Name des Sprungknopfes
            // bleibt so der ganze Anker, den die Testreihe über `role` und
            // `name` findet.
            fontSize: "var(--fs-14)",
            fontWeight: "var(--fw-semibold)",
            fontVariantNumeric: "tabular-nums",
            textAlign: "left",
            overflowWrap: "anywhere",
            cursor: "pointer",
          }}
        >
          {describeAnchor(anchorOfFinding(site.finding))}
        </button>

        <Chip>
          {site.found.length === 1 ? "1 Befund" : `${String(site.found.length)} Befunde`}
        </Chip>

        {/* Der Weg in den Code, ausgeschrieben. Er tut dasselbe wie ein Klick
            auf den Ort links — dort steht er als Sprung und ist deshalb nicht
            zu sehen; hier steht sein Name. Er führt nach oben, in die eine
            Codefläche dieser Ansicht, und markiert die Zeilen dieser Stelle. */}
        <Button
          variant="outline"
          size="sm"
          icon="code"
          onClick={() => onJump(site.finding)}
        >
          Im Code ansehen
        </Button>
      </div>

      {/* Der Handgriff steht im Kopf der Stelle, bei Ausschnitt und Anker, über
          den Befunden — und er steht in der Detailansicht, weil hier der Code
          steht: „Fehlalarm" aus einer Tabellenzeile heraus wäre ein Klick ins
          Blaue. Rand und Trennlinie kommen von hier: Derselbe Handgriff steht in
          der Gegenüberstellung in einer anderen Nachbarschaft. */}
      <div
        style={{
          padding: "var(--sp-4) var(--sp-5)",
          borderBottom: "1px solid var(--line-f)",
        }}
      >
        <VerdictBar found={site.found} busy={busy} onDecide={onDecide} onWithdraw={onWithdraw} />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--sp-5)",
          padding: "var(--sp-5)",
          background: marked ? "var(--bg-sunken)" : "transparent",
        }}
      >
        {site.excerpt === null ? null : <Excerpt excerpt={site.excerpt} />}

        {/* Der Zeitverlauf trägt einen Namen, seit im Kopf darüber der
            Handgriff steht: Beide Flächen sprechen von derselben Stelle, und
            ohne Namen wäre nicht zu sagen, ob ein „Fehlalarm" hier der gesetzte
            Zustand oder der Knopf ist, der ihn setzt. */}
        <ol
          aria-label="Befunde dieser Stelle"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--sp-4)",
            margin: 0,
            padding: 0,
            listStyle: "none",
          }}
        >
          {site.events.map((event) =>
            event.kind === "finding" ? (
              <FindingEvent
                key={`f${String(event.finding.id)}`}
                finding={event.finding}
                editable={event.finding.own && !submitted && !busy}
                busy={busy}
                followUp={followUpOf(followUps, event.finding.id)}
                handle={patternHandle(event.finding)}
                onFollowUp={() => onFollowUp(event.finding)}
                onWithdrawFollowUp={() => onWithdrawFollowUp(event.finding)}
                onEdit={() => onEdit(event.finding)}
                onDelete={() => onDelete(event.finding)}
              />
            ) : (
              <RefutationEvent
                key={`r${String(event.finding.id)}-${String(event.order)}`}
                refutation={event.refutation}
              />
            ),
          )}
        </ol>
      </div>
    </article>
  );
}

/** Ein Befund im Zeitverlauf: Zeit, Urheber, Einstufung, Text. */
function FindingEvent({
  finding,
  editable,
  busy,
  followUp,
  handle,
  onFollowUp,
  onWithdrawFollowUp,
  onEdit,
  onDelete,
}: {
  finding: RevisionFinding;
  editable: boolean;
  busy: boolean;
  /** Wie viele diesen Befund nachgehalten haben wollen — und ob man selbst dabei ist. */
  followUp: FollowUpTally;
  /** Der Knopf „Muster daraus machen" — `null`, wo keiner dasteht. */
  handle: ReactElement | null;
  onFollowUp: () => void;
  onWithdrawFollowUp: () => void;
  onEdit: () => void;
  onDelete: () => void;
}): ReactElement {
  const judged = labelOfJudgement(finding.qualifiedBy, finding.qualifiedAt);

  // Farbe oder keine — das ist seit #141 die ganze Fallunterscheidung: Was einen
  // Ton trägt, ist ein Zustand und steht als `StatusPill` da, was farblos bleibt
  // („hinweis", „zur Kenntnis") ist ein Etikett und steht als `Chip` da.
  const severityTone = SEVERITY_TONES[finding.severity];
  const statusTone = STATUS_TONES[finding.status];
  const qualificationTone = finding.qualification === null ? null : QUALIFICATION_TONES[finding.qualification];

  return (
    <li style={{ display: "flex", gap: "var(--sp-5)" }}>
      <Clock at={finding.createdAt} />

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <div
          style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--sp-3)" }}
        >
          <span
            title={`${ORIGINS[finding.origin]}${finding.own ? ", Ihr eigener Befund" : ""}`}
            style={{
              fontSize: "var(--fs-12)",
              fontWeight: "var(--fw-medium)",
              color: finding.own ? "var(--fg-1)" : "var(--text-body)",
              overflowWrap: "anywhere",
            }}
          >
            {finding.author}
          </span>
          <StatusPill tone="blue">{finding.kind}</StatusPill>
          {severityTone === null ? (
            <Chip>{finding.severity}</Chip>
          ) : (
            <StatusPill tone={severityTone}>{finding.severity}</StatusPill>
          )}
          {/* Beide Achsen nebeneinander (Spec v1.4, Story 253): „bestätigt und
              offen" ist der Normalfall, und keine der beiden Angaben darf die
              andere verdrängen. Weggelassen wird der Lebenszyklus bei `open`
              und die Qualifizierung, solange niemand geurteilt hat — beides
              stünde sonst an jeder Zeile und sagte nichts. */}
          {finding.status === "open" ? null : statusTone === null ? (
            <Chip>{STATUS_LABELS[finding.status]}</Chip>
          ) : (
            <StatusPill tone={statusTone}>{STATUS_LABELS[finding.status]}</StatusPill>
          )}
          {finding.qualification === null ? null : qualificationTone === null ? (
            <Chip>{QUALIFICATION_LABELS[finding.qualification]}</Chip>
          ) : (
            <StatusPill tone={qualificationTone}>{QUALIFICATION_LABELS[finding.qualification]}</StatusPill>
          )}

          <span style={{ flex: 1 }} />

          {/* **Nachhalten steht am einzelnen Befund** und nicht an der Stelle
              (ADR-0012): Vermerkt wird, dass die Erkennung für *diese*
              Beobachtung sensibilisiert sein soll — an einer Stelle, die drei
              Agenten und ein Kontrollmuster tragen, wäre nicht zu sagen,
              welche gemeint ist. Am Kontrollbefund wird gar nicht erst
              gefragt: Er *ist* schon ein Muster. */}
          {followable(finding.origin) ? (
            <FollowUp
              tally={followUp}
              busy={busy}
              onMark={onFollowUp}
              onWithdraw={onWithdrawFollowUp}
            />
          ) : null}

          {/* **Aus diesem Befund ein Kontrollmuster** (ADR-0033, ADR-0012): am
              einzelnen Befund und nicht an der Stelle, denn ein Muster entsteht
              aus *einer* Beobachtung — an einer Stelle, die drei Agenten und ein
              Kontrollmuster tragen, wäre nicht zu sagen, welche gemeint ist.
              Derselbe Zuschnitt wie beim Nachhalten daneben. */}
          {handle}

          {/* **Hier wird nichts gesetzt.** Der Handgriff gilt für die Stelle und
              steht in ihrem Kopf (ADR-0022); hier stünde er an einem einzelnen
              Befund und ließe die übrigen unbeurteilt. Geändert und gelöscht
              wird weiter nur der eigene Befund — das ist keine Bewertung,
              sondern der Text, den jemand geschrieben hat. */}
          {editable ? (
            <>
              <IconButton icon="pencil" tip="Befund ändern" size="sm" tone="quiet" onClick={onEdit} />
              <IconButton icon="trash-2" tip="Befund löschen" size="sm" tone="quiet" onClick={onDelete} />
            </>
          ) : null}
        </div>

        {/* Wer wann bewertet hat, unter den Zeichen (Spec v1.4, Story 257) —
            hier je Befund, weil gespeichert je Befund wird. Gesetzt wird im
            Kopf der Stelle: Ein Urteil, das niemand unterschreibt, kann niemand
            hinterfragen (ADR-0022). */}
        {judged === null ? null : (
          <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}>{judged}</span>
        )}

        {/* Der Titel über dem Freitext, wo einer abgelegt ist. Er ist dieselbe
            Zeile, die in der Befundliste steht — wer von dort kommt, findet
            hier wörtlich wieder, worauf er geklickt hat. */}
        {finding.title === "" ? null : (
          <span
            style={{
              maxWidth: "84ch",
              fontSize: "var(--fs-12)",
              fontWeight: "var(--fw-medium)",
              color: "var(--fg-1)",
              textWrap: "pretty",
            }}
          >
            {finding.title}
          </span>
        )}

        {/* Der Befundtext bekommt die Breite, um die es bei diesem Umbau geht —
            begrenzt auf eine lesbare Zeilenlänge und nicht auf eine Spalte. */}
        <span
          style={{
            maxWidth: "84ch",
            fontSize: "var(--fs-12)",
            lineHeight: "var(--lh-normal)",
            color: "var(--text-body)",
            textWrap: "pretty",
          }}
        >
          {finding.body}
        </span>

        {/* Die Folge steht unter dem Freitext, mit knapper Beschriftung — und
            nur, wo der Befund eine trägt. Altbestand und Kontrollbefunde
            bekommen hier keine erfundene hin. */}
        {finding.consequence === null ? null : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-1)" }}>
            <span
              style={{
                fontSize: "var(--fs-11)",
                fontWeight: "var(--fw-medium)",
                color: "var(--fg-6)",
              }}
            >
              Folge
            </span>
            <span
              style={{
                maxWidth: "84ch",
                fontSize: "var(--fs-12)",
                lineHeight: "var(--lh-normal)",
                color: "var(--text-body)",
                textWrap: "pretty",
              }}
            >
              {finding.consequence}
            </span>
          </div>
        )}

        {/* Der Vorschlag steht unter der Folge, in derselben Gestalt und mit
            denselben Maßen — und nur, wo einer abgelegt ist. „Vorschlag" und
            nicht „Behebung": Im Wort soll stehen, dass es ein Vorschlag ist,
            denn „behoben" ist der Ausgang des Lebenszyklus. */}
        {finding.remedy === null ? null : (
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-1)" }}>
            <span
              style={{
                fontSize: "var(--fs-11)",
                fontWeight: "var(--fw-medium)",
                color: "var(--fg-6)",
              }}
            >
              Vorschlag
            </span>
            <span
              style={{
                maxWidth: "84ch",
                fontSize: "var(--fs-12)",
                lineHeight: "var(--lh-normal)",
                color: "var(--text-body)",
                textWrap: "pretty",
              }}
            >
              {finding.remedy}
            </span>
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Eine Widerlegung im Zeitverlauf.
 *
 * Sie steht eingerückt unter dem Befund, auf den sie sich bezieht, und trägt
 * ihre Begründung im Klartext. Vorher war sie ein Schild, dessen Text man nur
 * beim Draufzeigen las — bei einer Aussage, die dem Befund widerspricht, ist das
 * zu wenig.
 */
function RefutationEvent({ refutation }: { refutation: Refutation }): ReactElement {
  // „unklar" trägt keine Farbe: Der Auftrag lautet zu widerlegen, und ein
  // Versuch ohne Ergebnis ist kein Zustand des Befundes.
  const tone = REFUTATION_TONES[refutation.result];

  return (
    <li style={{ display: "flex", gap: "var(--sp-5)", paddingLeft: "var(--sp-6)" }}>
      <Clock at={refutation.createdAt} />

      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--sp-3)" }}>
          <Icon name="corner-down-right" size={13} />
          <span style={{ fontSize: "var(--fs-12)", color: "var(--text-body)" }}>
            Widerlegung von {refutation.author}
          </span>
          {tone === null ? (
            <Chip>{REFUTATION_LABELS[refutation.result]}</Chip>
          ) : (
            <StatusPill tone={tone}>{REFUTATION_LABELS[refutation.result]}</StatusPill>
          )}
        </div>

        <span
          style={{
            maxWidth: "84ch",
            fontSize: "var(--fs-12)",
            lineHeight: "var(--lh-normal)",
            color: "var(--fg-6)",
            textWrap: "pretty",
          }}
        >
          {refutation.reason}
        </span>
      </div>
    </li>
  );
}

/**
 * Die Zeitspalte. Sie hat eine feste Breite, damit die Einträge untereinander
 * fluchten — ein Zeitverlauf, dessen Spalte mitwandert, ist keiner.
 */
function Clock({ at }: { at: string | null }): ReactElement {
  return (
    <span
      title={at === null ? undefined : formatTimestamp(at)}
      style={{
        width: "5ch",
        flex: "none",
        fontSize: "var(--fs-11)",
        fontVariantNumeric: "tabular-nums",
        color: "var(--fg-6)",
      }}
    >
      {at === null ? "—" : formatClock(at)}
    </span>
  );
}

/**
 * Das Formular als Dialog: Anker, Befundart, Schweregrad, Freitext.
 *
 * Als Dialog und nicht mehr als Kasten in einer Leiste — die Leiste gibt es
 * nicht mehr, und ein Formular, das dauerhaft Platz belegt, nähme genau die
 * Breite weg, um die es bei diesem Umbau geht.
 *
 * Befundart und Schweregrad haben keine Vorbelegung. Eine vorbelegte Befundart
 * wäre die, die am häufigsten stünde — und die Auswertung, für die diese Befunde
 * erhoben werden, läse das als Urteil.
 */
function Composer({
  draft,
  anchor,
  busy,
  onClose,
  onSave,
}: {
  draft: Draft;
  anchor: FindingAnchor | null;
  busy: boolean;
  onClose: () => void;
  onSave: (draft: Draft) => void;
}): ReactElement {
  const [current, setCurrent] = useState<Draft>(draft);
  const complete =
    anchor !== null &&
    current.kind !== null &&
    current.severity !== null &&
    current.title.trim() !== "" &&
    current.body.trim() !== "";

  return (
    <Modal onClose={onClose}>
      <Dialog
        title={draft.id === null ? "Befund anlegen" : "Befund ändern"}
        description={anchor === null ? NOTHING_MARKED : describeAnchor(anchor)}
        width={720}
        onClose={onClose}
        footer={
          <>
            <Button variant="outline" size="lg" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              variant="primary"
              size="lg"
              disabled={busy || !complete}
              onClick={() => onSave(current)}
            >
              {draft.id === null ? "Befund anlegen" : "Änderung speichern"}
            </Button>
          </>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-4)" }}>
          {/* Der Titel steht oben, weil er in der Befundliste die Zeile trägt:
              Wer ihn dort liest, muss wissen, worum es geht, ohne den Befund zu
              öffnen. Pflicht ist er wie Befundart und Schweregrad — ein Befund
              ohne Titel stünde in der Liste stumm da. Beschriftet und nicht nur
              mit Platzhalter: Der verschwindet beim Tippen, und danach weiß
              niemand mehr, was in dem Feld steht. */}
          <label
            htmlFor="finding-title"
            style={{ fontSize: "var(--fs-11)", color: "var(--fg-6)" }}
          >
            Titel
          </label>
          <TextField
            id="finding-title"
            style={{ width: "100%" }}
            value={current.title}
            placeholder="Titel — eine Zeile, die sagt, worum es geht"
            // Gekappt und nicht bloß gewarnt: Dieselbe Grenze gilt für die
            // Agenten, und eine Zeile, die in der Liste abgeschnitten ankommt,
            // hätte man auch hier schon kürzen können.
            onChange={(typed) => setCurrent({ ...current, title: typed.slice(0, TITLE_LIMIT) })}
          />

          <div style={{ display: "flex", gap: "var(--sp-4)" }}>
            <Select
              options={KINDS}
              value={current.kind ?? ""}
              placeholder="Befundart wählen ..."
              onSelect={(chosen) => setCurrent({ ...current, kind: asKind(chosen) ?? current.kind })}
            />

            <Select
              options={SEVERITIES}
              value={current.severity ?? ""}
              placeholder="Schweregrad wählen ..."
              onSelect={(chosen) =>
                setCurrent({ ...current, severity: asSeverity(chosen) ?? current.severity })
              }
            />
          </div>

          <textarea
            aria-label="Freitext"
            rows={6}
            value={current.body}
            placeholder="Was ist Ihnen aufgefallen?"
            onChange={(event) => setCurrent({ ...current, body: event.target.value })}
            className="erp-scroll"
            style={{
              width: "100%",
              padding: "var(--sp-3) var(--sp-4)",
              background: "var(--surface-field)",
              border: "1px solid var(--border-field)",
              borderRadius: "var(--r-field)",
              color: "var(--fg-1)",
              fontFamily: "var(--font-sans)",
              fontSize: "var(--fs-12)",
              lineHeight: "var(--lh-normal)",
              resize: "vertical",
              outline: "none",
            }}
          />
        </div>
      </Dialog>
    </Modal>
  );
}

function asKind(value: string): FindingKind | null {
  return KINDS.find((kind) => kind === value) ?? null;
}

function asSeverity(value: string): Severity | null {
  return SEVERITIES.find((severity) => severity === value) ?? null;
}
